import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    Box,
    Container,
    Typography,
    Paper,
    Button,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Chip,
    Alert,
    CircularProgress,
    Tooltip,
    Stack,
    Tabs,
    Tab,
    ToggleButtonGroup,
    ToggleButton,
    List,
    ListItemButton,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    ArrowBack,
    ContentCopy,
    WhatsApp,
    Refresh,
    CheckCircle,
    RestartAlt,
    Download,
    Image as ImageIcon,
    Message as MessageIcon,
    GridView,
} from "@mui/icons-material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import {
    toolsService,
    PriceListProduct,
    CategoryWithCount,
    BrandWithCount,
} from "../../services/toolsService";
import { MobilePreview } from "./components/MobilePreview";
import { ProductCard } from "./components/ProductCard";
import { ProductCollage, MAX_COLLAGE_PRODUCTS } from "./components/ProductCollage";
import {
    CategoryChainBuilder,
    CategoryTerm,
    categoryChainToGroups,
} from "./components/CategoryChainBuilder";
import {
    loadPersisted,
    usePersistedEffect,
    clearPersisted,
} from "../../hooks/useLocalStoragePersistence";
import {
    exportElementAsPng,
    copyElementToClipboard,
    slugifyFilename,
} from "../../utils/exportCardImage";

// Everything a moderator builds up while using this tool — filters, the
// current product selection, and message customization — persists across
// reloads/navigation so in-progress work is never silently lost. The loaded
// `categories`/`brands`/`products` lists themselves are intentionally NOT
// persisted here; those are always re-fetched fresh so prices/stock/tags
// can't go stale in localStorage.
const STORAGE_KEY = "cps-admin-price-list-generator-state";

interface PersistedPriceListState {
    categoryChain: CategoryTerm[];
    selectedBrands: string[];
    minPrice: string;
    maxPrice: string;
    activeOnly: boolean;
    inStockOnly: boolean;
    searchQuery: string;
    selectedProducts: string[];
    messageFormat: "simple" | "categorized" | "detailed" | "promotional";
    includeEmojis: boolean;
    includeStock: boolean;
    customHeader: string;
    customFooter: string;
    generatedMessage: string;
    characterCount: number;
}

const DEFAULT_PERSISTED_STATE: PersistedPriceListState = {
    categoryChain: [],
    selectedBrands: [],
    minPrice: "",
    maxPrice: "",
    activeOnly: true,
    inStockOnly: false,
    searchQuery: "",
    selectedProducts: [],
    messageFormat: "categorized",
    includeEmojis: true,
    includeStock: false,
    customHeader: "",
    customFooter: "",
    generatedMessage: "",
    characterCount: 0,
};

export const PriceListGenerator: React.FC = () => {
    const navigate = useNavigate();

    // Restored once on mount; individual fields below seed their initial
    // useState value from this so every existing setter keeps working
    // exactly as before — persistence is purely additive.
    const restored = useMemo(
        () => loadPersisted(STORAGE_KEY, DEFAULT_PERSISTED_STATE),
        [],
    );

    // State
    const [categories, setCategories] = useState<CategoryWithCount[]>([]);
    const [brands, setBrands] = useState<BrandWithCount[]>([]);
    const theme = useTheme();
    // Phones get a tick-list — the grid's columns don't fit a phone.
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [products, setProducts] = useState<PriceListProduct[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<string[]>(
        restored.selectedProducts,
    );
    const [generatedMessage, setGeneratedMessage] = useState(
        restored.generatedMessage,
    );
    const [characterCount, setCharacterCount] = useState(
        restored.characterCount,
    );
    const [copied, setCopied] = useState(false);

    // Filters
    const [categoryChain, setCategoryChain] = useState<CategoryTerm[]>(
        restored.categoryChain,
    );
    const [selectedBrands, setSelectedBrands] = useState<string[]>(
        restored.selectedBrands,
    );
    const [minPrice, setMinPrice] = useState<string>(restored.minPrice);
    const [maxPrice, setMaxPrice] = useState<string>(restored.maxPrice);
    const [activeOnly, setActiveOnly] = useState(restored.activeOnly);
    const [inStockOnly, setInStockOnly] = useState(restored.inStockOnly);
    const [searchQuery, setSearchQuery] = useState(restored.searchQuery);

    // Message customization
    const [messageFormat, setMessageFormat] = useState<
        "simple" | "categorized" | "detailed" | "promotional"
    >(restored.messageFormat);
    const [includeEmojis, setIncludeEmojis] = useState(restored.includeEmojis);
    const [includeStock, setIncludeStock] = useState(restored.includeStock);
    const [customHeader, setCustomHeader] = useState(restored.customHeader);
    const [customFooter, setCustomFooter] = useState(restored.customFooter);

    // Loading states
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Right panel: text message vs. product-card export. Not persisted —
    // purely a view choice, not in-progress work worth restoring.
    const [rightTab, setRightTab] = useState<"message" | "cards">("message");
    const [downloadingCardId, setDownloadingCardId] = useState<string | null>(null);
    const [downloadingAll, setDownloadingAll] = useState(false);
    const [copyingCardId, setCopyingCardId] = useState<string | null>(null);
    const [copiedCardId, setCopiedCardId] = useState<string | null>(null);
    const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

    // Individual cards vs. one combined collage image — same selection,
    // different export target. Not persisted, same reasoning as rightTab.
    const [cardsMode, setCardsMode] = useState<"individual" | "collage">("individual");
    const collageRef = useRef<HTMLDivElement>(null);
    const [downloadingCollage, setDownloadingCollage] = useState(false);
    const [copyingCollage, setCopyingCollage] = useState(false);
    const [copiedCollage, setCopiedCollage] = useState(false);

    // Persist the working state on every change (debounced internally)
    usePersistedEffect(STORAGE_KEY, {
        categoryChain,
        selectedBrands,
        minPrice,
        maxPrice,
        activeOnly,
        inStockOnly,
        searchQuery,
        selectedProducts,
        messageFormat,
        includeEmojis,
        includeStock,
        customHeader,
        customFooter,
        generatedMessage,
        characterCount,
    } satisfies PersistedPriceListState);

    const resetFilters = () => {
        setCategoryChain(DEFAULT_PERSISTED_STATE.categoryChain);
        setSelectedBrands(DEFAULT_PERSISTED_STATE.selectedBrands);
        setMinPrice(DEFAULT_PERSISTED_STATE.minPrice);
        setMaxPrice(DEFAULT_PERSISTED_STATE.maxPrice);
        setActiveOnly(DEFAULT_PERSISTED_STATE.activeOnly);
        setInStockOnly(DEFAULT_PERSISTED_STATE.inStockOnly);
        setSearchQuery(DEFAULT_PERSISTED_STATE.searchQuery);
        setProducts([]);
        clearPersisted(STORAGE_KEY);
    };

    // Load categories and brands on mount
    useEffect(() => {
        loadFiltersData();
        // Seamlessly resume a restored session — if there was a real filter
        // in progress, re-fetch the matching products right away instead of
        // showing an empty grid until "Load Products" is clicked again.
        const hasRestoredFilters =
            restored.categoryChain.length > 0 ||
            restored.selectedBrands.length > 0 ||
            !!restored.minPrice ||
            !!restored.maxPrice ||
            !!restored.searchQuery ||
            restored.inStockOnly;
        if (hasRestoredFilters) {
            loadProducts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadFiltersData = async () => {
        try {
            const [categoriesData, brandsData] = await Promise.all([
                toolsService.getCategoriesForPriceList(),
                toolsService.getBrandsForPriceList(),
            ]);
            setCategories(categoriesData);
            setBrands(brandsData);
        } catch (err: any) {
            setError("Failed to load filters: " + err.message);
        }
    };

    // Load products when filters change
    const loadProducts = async () => {
        setLoading(true);
        setError(null);
        try {
            const filters = {
                category_groups:
                    categoryChain.length > 0
                        ? categoryChainToGroups(categoryChain)
                        : undefined,
                brand_ids:
                    selectedBrands.length > 0 ? selectedBrands : undefined,
                min_price: minPrice ? parseFloat(minPrice) : undefined,
                max_price: maxPrice ? parseFloat(maxPrice) : undefined,
                active_only: activeOnly,
                in_stock_only: inStockOnly,
                search_query: searchQuery || undefined,
            };
            const productsData =
                await toolsService.filterProductsForPriceList(filters);
            setProducts(productsData);
        } catch (err: any) {
            setError("Failed to load products: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Generate message
    const generateMessage = async () => {
        if (selectedProducts.length === 0) {
            setError("Please select at least one product");
            return;
        }

        setGenerating(true);
        setError(null);
        try {
            const request = {
                product_ids: selectedProducts,
                format: messageFormat,
                include_emojis: includeEmojis,
                include_stock: includeStock,
                custom_header: customHeader || undefined,
                custom_footer: customFooter || undefined,
            };
            const response =
                await toolsService.generatePriceListMessage(request);
            setGeneratedMessage(response.message);
            setCharacterCount(response.character_count);
        } catch (err: any) {
            setError("Failed to generate message: " + err.message);
        } finally {
            setGenerating(false);
        }
    };

    // Handle row selection changes
    const handleSelectionChange = (newSelection: any) => {
        console.log(
            "Selection changed:",
            newSelection,
            "Type:",
            typeof newSelection,
            "IsArray:",
            Array.isArray(newSelection),
        );

        // Convert to string array - DataGrid can pass array or object format
        if (Array.isArray(newSelection)) {
            console.log("Handling as array, length:", newSelection.length);
            setSelectedProducts(newSelection.map((id) => String(id)));
        } else if (
            newSelection &&
            typeof newSelection === "object" &&
            "ids" in newSelection
        ) {
            const idsArray = Array.from(newSelection.ids).map((id) =>
                String(id),
            );
            console.log(
                "Handling as object with ids Set, size:",
                newSelection.ids.size,
                "converted array length:",
                idsArray.length,
            );
            setSelectedProducts(idsArray);
        } else {
            console.log("Clearing selection");
            setSelectedProducts([]);
        }
    };

    // Copy to clipboard
    const copyToClipboard = () => {
        navigator.clipboard.writeText(generatedMessage);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Open in WhatsApp
    const openInWhatsApp = () => {
        const encodedMessage = encodeURIComponent(generatedMessage);
        window.open(`https://wa.me/?text=${encodedMessage}`, "_blank");
    };

    // Products currently selected in the grid, resolved from the loaded
    // list — the Product Cards tab renders one card per entry here.
    const selectedProductObjects = products.filter((p) =>
        selectedProducts.includes(p.product_id),
    );

    const downloadCard = async (product: PriceListProduct) => {
        const element = cardRefs.current.get(product.product_id);
        if (!element) return;
        setDownloadingCardId(product.product_id);
        try {
            await exportElementAsPng(element, slugifyFilename(product.name));
        } catch (err: any) {
            setError("Failed to export card: " + err.message);
        } finally {
            setDownloadingCardId(null);
        }
    };

    const copyCard = async (product: PriceListProduct) => {
        const element = cardRefs.current.get(product.product_id);
        if (!element) return;
        setCopyingCardId(product.product_id);
        try {
            await copyElementToClipboard(element);
            setCopiedCardId(product.product_id);
            setTimeout(() => setCopiedCardId(null), 2000);
        } catch (err: any) {
            setError("Failed to copy card: " + err.message);
        } finally {
            setCopyingCardId(null);
        }
    };

    const downloadAllCards = async () => {
        setDownloadingAll(true);
        setError(null);
        try {
            // Sequential, not Promise.all — triggering many downloads at
            // once gets throttled/blocked by some browsers, and a small
            // gap between clicks avoids that reliably.
            for (const product of selectedProductObjects) {
                const element = cardRefs.current.get(product.product_id);
                if (!element) continue;
                await exportElementAsPng(element, slugifyFilename(product.name));
                await new Promise((resolve) => setTimeout(resolve, 350));
            }
        } catch (err: any) {
            setError("Failed to export cards: " + err.message);
        } finally {
            setDownloadingAll(false);
        }
    };

    const downloadCollage = async () => {
        if (!collageRef.current) return;
        setDownloadingCollage(true);
        setError(null);
        try {
            await exportElementAsPng(collageRef.current, "product-collage");
        } catch (err: any) {
            setError("Failed to export collage: " + err.message);
        } finally {
            setDownloadingCollage(false);
        }
    };

    const copyCollage = async () => {
        if (!collageRef.current) return;
        setCopyingCollage(true);
        setError(null);
        try {
            await copyElementToClipboard(collageRef.current);
            setCopiedCollage(true);
            setTimeout(() => setCopiedCollage(false), 2000);
        } catch (err: any) {
            setError("Failed to copy collage: " + err.message);
        } finally {
            setCopyingCollage(false);
        }
    };

    // Memoize the selection model for DataGrid
    const rowSelectionModel = useMemo(
        () => ({
            type: "include" as const,
            ids: new Set(selectedProducts),
        }),
        [selectedProducts],
    );

    // DataGrid columns
    const columns: GridColDef[] = [
        { field: "name", headerName: "Product Name", flex: 1, minWidth: 200 },
        {
            field: "price",
            headerName: "Price",
            width: 100,
            renderCell: (params) => `৳${params.value.toFixed(0)}`,
        },
        {
            field: "stock_quantity",
            headerName: "Stock",
            width: 80,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    color={
                        params.value > 10
                            ? "success"
                            : params.value > 0
                              ? "warning"
                              : "error"
                    }
                />
            ),
        },
    ];

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 0, sm: 4 }, px: { xs: 0, sm: 3 } }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate("/tools")}
                    sx={{ mb: 2 }}
                >
                    Back to Tools
                </Button>
                <Typography variant="h4" component="h1" gutterBottom>
                    Price List Generator
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Generate formatted price lists for social media and customer
                    service
                </Typography>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Left Panel - Filters and Products */}
                <Grid size={{ xs: 12, lg: 7 }}>
                    {/* Filters */}
                    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Filters
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <CategoryChainBuilder
                                    categories={categories}
                                    chain={categoryChain}
                                    onChange={setCategoryChain}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Brands</InputLabel>
                                    <Select
                                        multiple
                                        value={selectedBrands}
                                        onChange={(e) =>
                                            setSelectedBrands(
                                                e.target.value as string[],
                                            )
                                        }
                                        renderValue={(selected) => (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 0.5,
                                                }}
                                            >
                                                {selected.map((value) => (
                                                    <Chip
                                                        key={value}
                                                        label={
                                                            brands.find(
                                                                (b) =>
                                                                    b.brand_id ===
                                                                    value,
                                                            )?.name
                                                        }
                                                        size="small"
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    >
                                        {brands.map((brand) => (
                                            <MenuItem
                                                key={brand.brand_id}
                                                value={brand.brand_id}
                                            >
                                                {brand.name} (
                                                {brand.product_count})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Min Price"
                                    type="number"
                                    value={minPrice}
                                    onChange={(e) =>
                                        setMinPrice(e.target.value)
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Max Price"
                                    type="number"
                                    value={maxPrice}
                                    onChange={(e) =>
                                        setMaxPrice(e.target.value)
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Search products..."
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Stack direction="row" spacing={1}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={activeOnly}
                                                onChange={(e) =>
                                                    setActiveOnly(
                                                        e.target.checked,
                                                    )
                                                }
                                                size="small"
                                            />
                                        }
                                        label="Active Only"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={inStockOnly}
                                                onChange={(e) =>
                                                    setInStockOnly(
                                                        e.target.checked,
                                                    )
                                                }
                                                size="small"
                                            />
                                        }
                                        label="In Stock"
                                    />
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Stack direction="row" spacing={1.5}>
                                    <Button
                                        variant="contained"
                                        onClick={loadProducts}
                                        disabled={loading}
                                        startIcon={
                                            loading ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <Refresh />
                                            )
                                        }
                                    >
                                        {loading
                                            ? "Loading..."
                                            : "Load Products"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={resetFilters}
                                        startIcon={<RestartAlt />}
                                    >
                                        Reset Filters
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Products Table */}
                    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                            }}
                        >
                            <Typography variant="h6">
                                Products ({products.length})
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedProducts.length} selected
                            </Typography>
                        </Box>
                        {isMobile ? (
                            <>
                                {products.length > 0 && (
                                    <Button
                                        size="small"
                                        sx={{ mb: 1, ml: -0.5 }}
                                        onClick={() =>
                                            setSelectedProducts(
                                                selectedProducts.length === products.length
                                                    ? []
                                                    : products.map((p) => p.product_id),
                                            )
                                        }
                                    >
                                        {selectedProducts.length === products.length ? "Clear selection" : "Select all"}
                                    </Button>
                                )}
                                <List
                                    dense
                                    disablePadding
                                    sx={{ maxHeight: 500, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}
                                >
                                    {products.length === 0 && (
                                        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                                            Pick filters and tap Load Products.
                                        </Typography>
                                    )}
                                    {products.map((p) => {
                                        const checked = selectedProducts.includes(p.product_id);
                                        return (
                                            <ListItemButton
                                                key={p.product_id}
                                                divider
                                                selected={checked}
                                                onClick={() =>
                                                    setSelectedProducts(
                                                        checked
                                                            ? selectedProducts.filter((id) => id !== p.product_id)
                                                            : [...selectedProducts, p.product_id],
                                                    )
                                                }
                                                sx={{ gap: 1, alignItems: "flex-start", py: 1 }}
                                            >
                                                <Checkbox checked={checked} size="small" sx={{ p: 0.25 }} tabIndex={-1} />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                                                        {p.name}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        ৳{p.price.toFixed(0)}
                                                    </Typography>
                                                    <Chip
                                                        label={`${p.stock_quantity} in stock`}
                                                        size="small"
                                                        variant="outlined"
                                                        color={p.stock_quantity > 10 ? "success" : p.stock_quantity > 0 ? "warning" : "error"}
                                                        sx={{ height: 20, fontSize: 11, mt: 0.25 }}
                                                    />
                                                </Box>
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </>
                        ) : (
                        <Box sx={{ height: 500, width: "100%" }}>
                            <DataGrid
                                rows={products}
                                columns={columns}
                                getRowId={(row) => row.product_id}
                                checkboxSelection
                                disableRowSelectionOnClick
                                rowSelectionModel={rowSelectionModel as any}
                                onRowSelectionModelChange={
                                    handleSelectionChange
                                }
                                pageSizeOptions={[10, 25, 50, 100]}
                                initialState={{
                                    pagination: {
                                        paginationModel: { pageSize: 25 },
                                    },
                                }}
                            />
                        </Box>
                        )}
                    </Paper>
                </Grid>

                {/* Right Panel - Message Generation and Preview */}
                <Grid size={{ xs: 12, lg: 5 }}>
                    <Paper sx={{ mb: 3 }}>
                        <Tabs
                            value={rightTab}
                            onChange={(_, value) => {
                                // Opening Product Cards with several products
                                // selected used to always land on Individual,
                                // which eagerly renders (and fetches images
                                // for) every one of them even if the user
                                // actually wanted the single combined
                                // Collage image. Pick the mode that matches
                                // what's selected right now instead — only
                                // on this tab switch, not on every selection
                                // change, so it doesn't fight a manual choice
                                // made while already on the tab.
                                if (value === "cards") {
                                    setCardsMode(
                                        selectedProductObjects.length > 1 ? "collage" : "individual",
                                    );
                                }
                                setRightTab(value);
                            }}
                            variant="fullWidth"
                        >
                            <Tab value="message" icon={<MessageIcon />} iconPosition="start" label="Text Message" />
                            <Tab value="cards" icon={<ImageIcon />} iconPosition="start" label="Product Cards" />
                        </Tabs>
                    </Paper>

                    {rightTab === "message" && (
                    <>
                    {/* Message Customization */}
                    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Message Customization
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Format</InputLabel>
                                    <Select
                                        value={messageFormat}
                                        onChange={(e) =>
                                            setMessageFormat(
                                                e.target.value as any,
                                            )
                                        }
                                    >
                                        <MenuItem value="simple">
                                            Simple List
                                        </MenuItem>
                                        <MenuItem value="categorized">
                                            Categorized
                                        </MenuItem>
                                        <MenuItem value="detailed">
                                            Detailed (with SKU)
                                        </MenuItem>
                                        <MenuItem value="promotional">
                                            Promotional
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeEmojis}
                                            onChange={(e) =>
                                                setIncludeEmojis(
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                    }
                                    label="Include Emojis"
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeStock}
                                            onChange={(e) =>
                                                setIncludeStock(
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                    }
                                    label="Include Stock"
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Custom Header (optional)"
                                    value={customHeader}
                                    onChange={(e) =>
                                        setCustomHeader(e.target.value)
                                    }
                                    placeholder="e.g., 🎉 Special Offer!"
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Custom Footer (optional)"
                                    value={customFooter}
                                    onChange={(e) =>
                                        setCustomFooter(e.target.value)
                                    }
                                    placeholder="e.g., 📞 Call: 01234-567890"
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={generateMessage}
                                    disabled={
                                        generating ||
                                        selectedProducts.length === 0
                                    }
                                    startIcon={
                                        generating ? (
                                            <CircularProgress size={20} />
                                        ) : null
                                    }
                                >
                                    {generating
                                        ? "Generating..."
                                        : "Generate Message"}
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Mobile Preview */}
                    {generatedMessage && (
                        <>
                            <MobilePreview
                                message={generatedMessage}
                                characterCount={characterCount}
                            />

                            {/* Actions */}
                            <Paper sx={{ p: 2, mt: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Share & Export
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip
                                        title={
                                            copied
                                                ? "Copied!"
                                                : "Copy to clipboard"
                                        }
                                    >
                                        <Button
                                            variant="outlined"
                                            startIcon={
                                                copied ? (
                                                    <CheckCircle />
                                                ) : (
                                                    <ContentCopy />
                                                )
                                            }
                                            onClick={copyToClipboard}
                                            color={
                                                copied ? "success" : "primary"
                                            }
                                        >
                                            {copied ? "Copied" : "Copy"}
                                        </Button>
                                    </Tooltip>
                                    <Button
                                        variant="outlined"
                                        startIcon={<WhatsApp />}
                                        onClick={openInWhatsApp}
                                        sx={{
                                            color: "#25D366",
                                            borderColor: "#25D366",
                                        }}
                                    >
                                        WhatsApp
                                    </Button>
                                </Stack>
                            </Paper>
                        </>
                    )}
                    </>
                    )}

                    {rightTab === "cards" && (
                        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <Typography variant="h6">
                                    Product Cards ({selectedProductObjects.length})
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                    <ToggleButtonGroup
                                        size="small"
                                        value={cardsMode}
                                        exclusive
                                        onChange={(_, value) => value && setCardsMode(value)}
                                    >
                                        <ToggleButton value="individual">
                                            <ImageIcon fontSize="small" sx={{ mr: 0.5 }} />
                                            Individual
                                        </ToggleButton>
                                        <ToggleButton value="collage">
                                            <GridView fontSize="small" sx={{ mr: 0.5 }} />
                                            Collage
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                    {cardsMode === "individual" && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={
                                                downloadingAll ? (
                                                    <CircularProgress size={16} color="inherit" />
                                                ) : (
                                                    <Download />
                                                )
                                            }
                                            disabled={
                                                downloadingAll ||
                                                selectedProductObjects.length === 0
                                            }
                                            onClick={downloadAllCards}
                                        >
                                            {downloadingAll ? "Downloading..." : "Download All"}
                                        </Button>
                                    )}
                                </Box>
                            </Box>

                            {selectedProductObjects.length === 0 ? (
                                <Alert severity="info">
                                    Select one or more products from the list to generate
                                    shareable cards.
                                </Alert>
                            ) : cardsMode === "collage" ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 2,
                                    }}
                                >
                                    {selectedProductObjects.length > MAX_COLLAGE_PRODUCTS && (
                                        <Alert severity="info" sx={{ width: "100%", maxWidth: 480 }}>
                                            Showing the first {MAX_COLLAGE_PRODUCTS} of{" "}
                                            {selectedProductObjects.length} selected products in
                                            the collage.
                                        </Alert>
                                    )}
                                    <ProductCollage
                                        products={selectedProductObjects}
                                        ref={collageRef}
                                    />
                                    <Box sx={{ display: "flex", gap: 1, width: 300 }}>
                                        <Button
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            color={copiedCollage ? "success" : "primary"}
                                            startIcon={
                                                copyingCollage ? (
                                                    <CircularProgress size={14} />
                                                ) : copiedCollage ? (
                                                    <CheckCircle fontSize="small" />
                                                ) : (
                                                    <ContentCopy fontSize="small" />
                                                )
                                            }
                                            disabled={copyingCollage}
                                            onClick={copyCollage}
                                        >
                                            {copiedCollage ? "Copied" : "Copy"}
                                        </Button>
                                        <Button
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            startIcon={
                                                downloadingCollage ? (
                                                    <CircularProgress size={14} />
                                                ) : (
                                                    <Download fontSize="small" />
                                                )
                                            }
                                            disabled={downloadingCollage}
                                            onClick={downloadCollage}
                                        >
                                            Download
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 2,
                                        justifyContent: "center",
                                    }}
                                >
                                    {selectedProductObjects.map((product) => (
                                        <Box
                                            key={product.product_id}
                                            sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                                        >
                                            <ProductCard
                                                product={product}
                                                ref={(el) => {
                                                    if (el) cardRefs.current.set(product.product_id, el);
                                                    else cardRefs.current.delete(product.product_id);
                                                }}
                                            />
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <Button
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    color={copiedCardId === product.product_id ? "success" : "primary"}
                                                    startIcon={
                                                        copyingCardId === product.product_id ? (
                                                            <CircularProgress size={14} />
                                                        ) : copiedCardId === product.product_id ? (
                                                            <CheckCircle fontSize="small" />
                                                        ) : (
                                                            <ContentCopy fontSize="small" />
                                                        )
                                                    }
                                                    disabled={copyingCardId === product.product_id}
                                                    onClick={() => copyCard(product)}
                                                >
                                                    {copiedCardId === product.product_id ? "Copied" : "Copy"}
                                                </Button>
                                                <Button
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={
                                                        downloadingCardId === product.product_id ? (
                                                            <CircularProgress size={14} />
                                                        ) : (
                                                            <Download fontSize="small" />
                                                        )
                                                    }
                                                    disabled={downloadingCardId === product.product_id}
                                                    onClick={() => downloadCard(product)}
                                                >
                                                    Download
                                                </Button>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Paper>
                    )}
                </Grid>
            </Grid>
        </Container>
    );
};
